import { useEffect, useRef, useState } from 'react';
import { advanceWorkingProgress, type WorkingEvent, type WorkingState } from './working-progress';

/** Recent runs by key, newest last, so a remounted or re-keyed screen resumes its run. */
const runsSeen = new Map<string, WorkingState>();

/** Forgets the runs keyed `prefix` or under it (a setup that was cleared), so a new run there starts from nothing. */
export function forgetWorkingRuns(prefix: string): void {
  for (const key of [...runsSeen.keys()])
    if (key === prefix || key.startsWith(`${prefix}:`)) runsSeen.delete(key);
}

/**
 * The working screen's one progress state, folded forward on every render from
 * the lanes the caller currently has. Folding is idempotent per event object, so
 * a re-render (or React's double render in development) with the same events
 * returns the same state.
 *
 * While `ticking`, it also owns the screen's one-second clock, so the step clock
 * moves every second whether or not the engine has spoken. Callers' own clocks
 * were anchored to engine events and could stand still between them. Off while
 * nothing is working, so a review workspace is not re-rendered every second.
 */
export function useWorkingProgress(
  runKey: string,
  lanes: ReadonlyArray<{ lane: string; event: WorkingEvent | null | undefined }>,
  ticking = true,
): { state: WorkingState; now: number } {
  const state = useRef<WorkingState | null>(null);
  // A run outlives the view drawing it: the screen's key flips between waves of
  // one import (nothing in flight for a moment), and the operator may leave the
  // job and come back mid-import. Either way the same run resumes where it was.
  const previous =
    state.current?.runKey === runKey ? state.current : (runsSeen.get(runKey) ?? null);
  state.current = advanceWorkingProgress(previous, runKey, lanes);
  runsSeen.delete(runKey);
  runsSeen.set(runKey, state.current);
  while (runsSeen.size > 12) runsSeen.delete(runsSeen.keys().next().value!);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!ticking) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [ticking]);
  return { state: state.current, now: Math.max(now, state.current.lastEventAt) };
}
